import { useEffect, useState } from "react";
import styled from "styled-components";

// ————— TWEAKS ENGINE —————
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/ {
    accent: "oklch(62% 0.22 268)",
    accent2: "oklch(68% 0.17 162)",
    bg: "dark",
    fontSize: 16,
    spacing: 140,
    font: "playfair",
}; /*EDITMODE-END*/

const FONT_PAIRS = {
    syne: {
        display: "'Syne', sans-serif",
        body: "'Plus Jakarta Sans', sans-serif",
    },
    playfair: {
        display: "'Playfair Display', serif",
        body: "'DM Sans', sans-serif",
    },
    space: {
        display: "'Space Grotesk', sans-serif",
        body: "'Manrope', sans-serif",
    },
    cormorant: { display: "'Cormorant', serif", body: "'Outfit', sans-serif" },
    barlow: {
        display: "'Barlow Condensed', sans-serif",
        body: "'Barlow', sans-serif",
    },
};

const TweaksPanel = styled.div.attrs({ id: "tweaks-panel" })`
    display: none;
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: var(--bg-card);
    border: 1px solid var(--border-dark);
    border-radius: 16px;
    padding: 20px;
    width: 260px;
    z-index: 9999;
    box-shadow: 0 24px 80px oklch(5% 0.04 260 / 0.7);
    font-family: var(--font-body);
    font-size: 13px;
    color: var(--text-dark);

    &.visible {
        display: block;
    }
`;

const TweakTitle = styled.h4`
    font-family: var(--font-display);
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 16px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
`;

const TweakRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
`;

const TweakLabel = styled.label`
    font-size: 11px;
    color: var(--text-muted-dark);
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

const TweakSelect = styled.select`
    width: 100%;
    background: var(--bg-dark);
    border: 1px solid var(--border-dark);
    border-radius: 8px;
    color: var(--text-dark);
    font-family: var(--font-body);
    font-size: 12px;
    padding: 6px 10px;
    cursor: pointer;
`;

const TweakRange = styled.input.attrs({ type: "range" })`
    width: 100%;
    background: var(--bg-dark);
    border: 1px solid var(--border-dark);
    border-radius: 8px;
    color: var(--text-dark);
    font-family: var(--font-body);
    font-size: 12px;
    padding: 4px;
    cursor: pointer;
    accent-color: var(--accent);
`;

const TweakColors = styled.div`
    display: flex;
    gap: 8px;
`;

const Swatch = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid transparent;
    transition: border-color 0.2s;

    &.active {
        border-color: white;
    }
`;

function applyTweaks(t) {
    const r = document.documentElement;
    r.style.setProperty("--accent", t.accent);
    r.style.setProperty("--accent-2", t.accent2);
    if (t.font && FONT_PAIRS[t.font]) {
        r.style.setProperty("--font-display", FONT_PAIRS[t.font].display);
        r.style.setProperty("--font-body", FONT_PAIRS[t.font].body);
    }
    r.style.setProperty("--section-gap", t.spacing + "px");
    document.body.style.fontSize = t.fontSize + "px";
    if (t.bg === "light") {
        r.style.setProperty("--bg-dark", "oklch(97% 0.006 255)");
        r.style.setProperty("--bg-card", "oklch(93% 0.008 255)");
        r.style.setProperty("--text-dark", "oklch(13% 0.02 260)");
        r.style.setProperty("--text-muted-dark", "oklch(40% 0.03 260)");
        r.style.setProperty("--border-dark", "oklch(85% 0.012 255)");
        document.body.style.background = "oklch(97% 0.006 255)";
    } else {
        r.style.setProperty("--bg-dark", "oklch(11% 0.02 260)");
        r.style.setProperty("--bg-card", "oklch(16% 0.02 260)");
        r.style.setProperty("--text-dark", "oklch(97% 0.005 255)");
        r.style.setProperty("--text-muted-dark", "oklch(65% 0.03 260)");
        r.style.setProperty("--border-dark", "oklch(22% 0.025 260)");
        document.body.style.background = "oklch(11% 0.02 260)";
    }
}

// ————— SHARED STYLES —————
const S = {
    section: { padding: "var(--section-gap, 100px) clamp(20px, 6vw, 80px)" },
    sectionLight: {
        padding: "var(--section-gap, 100px) clamp(20px, 6vw, 80px)",
        background: "var(--bg-light)",
        color: "var(--text-light)",
    },
    container: { maxWidth: 1200, margin: "0 auto" },
    label: {
        display: "inline-block",
        fontSize: 11,
        fontFamily: "var(--font-display)",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        fontWeight: 700,
        color: "var(--accent)",
        marginBottom: 16,
    },
    h1: {
        fontFamily: "var(--font-display)",
        fontSize: "clamp(48px, 6vw, 86px)",
        fontWeight: 800,
        lineHeight: 1.05,
        letterSpacing: "-0.02em",
    },
    h2: {
        fontFamily: "var(--font-display)",
        fontSize: "clamp(32px, 4vw, 54px)",
        fontWeight: 700,
        lineHeight: 1.1,
        letterSpacing: "-0.02em",
    },
    h3: {
        fontFamily: "var(--font-display)",
        fontSize: "clamp(20px, 2.5vw, 28px)",
        fontWeight: 700,
        lineHeight: 1.2,
    },
    body: {
        fontFamily: "var(--font-body)",
        fontSize: 16,
        lineHeight: 1.75,
        color: "var(--text-muted-dark)",
    },
    bodyLight: {
        fontFamily: "var(--font-body)",
        fontSize: 16,
        lineHeight: 1.75,
        color: "var(--text-muted-light)",
    },
};

// ————— COMPONENTS —————

function NavBar({ page, setPage }) {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { id: "home", label: "Accueil" },
        { id: "expertises", label: "Expertises" },
        { id: "realisations", label: "Réalisations" },
        { id: "contact", label: "Contact" },
    ];

    return (
        <nav
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                transition: "all 0.3s",
                background: scrolled
                    ? "oklch(11% 0.02 260 / 0.92)"
                    : "transparent",
                backdropFilter: scrolled ? "blur(20px)" : "none",
                borderBottom: scrolled
                    ? "1px solid var(--border-dark)"
                    : "1px solid transparent",
                padding: "0 clamp(20px, 5vw, 60px)",
            }}
        >
            <div
                style={{
                    maxWidth: 1200,
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    height: 72,
                    justifyContent: "space-between",
                }}
            >
                {/* Logo */}
                <button
                    onClick={() => {
                        setPage("home");
                        window.scrollTo(0, 0);
                    }}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <span
                        style={{
                            fontFamily: "var(--font-logo)",
                            fontWeight: 800,
                            fontSize: 30,
                            letterSpacing: "1px",
                            color: "var(--text-dark)",
                        }}
                    >
                        Alpes Logiciels
                    </span>
                </button>

                {/* Desktop Nav */}
                <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {links.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => {
                                setPage(l.id);
                                window.scrollTo(0, 0);
                            }}
                            style={{
                                border: "none",
                                cursor: "pointer",
                                padding: "8px 16px",
                                borderRadius: 8,
                                fontFamily: "var(--font-body)",
                                fontSize: 14,
                                fontWeight: 500,
                                color:
                                    page === l.id
                                        ? "var(--text-dark)"
                                        : "var(--text-muted-dark)",
                                background:
                                    page === l.id
                                        ? "var(--border-dark)"
                                        : "transparent",
                                transition: "all 0.2s",
                            }}
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        onClick={() => {
                            setPage("contact");
                            window.scrollTo(0, 0);
                        }}
                        style={{
                            marginLeft: 12,
                            padding: "10px 22px",
                            borderRadius: 100,
                            background: "var(--accent)",
                            border: "none",
                            cursor: "pointer",
                            fontFamily: "var(--font-body)",
                            fontSize: 14,
                            fontWeight: 600,
                            color: "white",
                            transition: "opacity 0.2s",
                        }}
                        onMouseOver={(e) => (e.target.style.opacity = "0.85")}
                        onMouseOut={(e) => (e.target.style.opacity = "1")}
                    >
                        Audit IA Gratuit
                    </button>
                </div>
            </div>
        </nav>
    );
}

// ORB background element
function Orbs() {
    return (
        <div
            style={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 0,
            }}
        >
            <div
                style={{
                    position: "absolute",
                    top: "-20%",
                    right: "-10%",
                    width: 700,
                    height: 700,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, oklch(62% 0.22 268 / 0.15) 0%, transparent 70%)",
                    filter: "blur(40px)",
                    animation: "orbFloat 8s ease-in-out infinite",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    bottom: "-10%",
                    left: "-10%",
                    width: 500,
                    height: 500,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, oklch(68% 0.17 162 / 0.1) 0%, transparent 70%)",
                    filter: "blur(50px)",
                    animation: "orbFloat 11s ease-in-out infinite reverse",
                }}
            />
            <style>{`
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-30px,30px) scale(1.05)} }
      `}</style>
        </div>
    );
}

// ————— PAGE: HOME —————
function PageHome({ setPage }) {
    const expertises = [
        {
            num: "01",
            title: "Applications Web Sur-Mesure",
            sub: "React · Node.js · PostgreSQL",
            body: "CRM internes, plateformes SaaS, portails métier — nous concevons des applications web robustes, évolutives et parfaitement adaptées à vos processus.",
            color: "var(--accent)",
        },
        {
            num: "02",
            title: "Automatisation & Intégration IA",
            sub: "LLM · Make · Zapier · API OpenAI",
            body: "Workflows intelligents, génération de contenu, extraction documentaire et agents IA intégrés directement dans vos outils et applications existants.",
            color: "var(--accent-2)",
        },
        {
            num: "03",
            title: "GEO & Référencement IA",
            sub: "ChatGPT · Gemini · Perplexity",
            body: "Optimisez votre visibilité dans les réponses des modèles génératifs. Vos futurs clients cherchent sur l'IA — soyez la réponse qu'ils obtiennent.",
            color: "var(--accent)",
        },
    ];

    return (
        <div className="page-enter">
            {/* HERO */}
            <section
                style={{
                    ...S.section,
                    position: "relative",
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: 120,
                }}
            >
                <Orbs />
                <div
                    style={{
                        ...S.container,
                        position: "relative",
                        zIndex: 1,
                        width: "100%",
                    }}
                >
                    <div style={{ maxWidth: 900 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginBottom: 28,
                            }}
                        >
                            <span
                                style={{
                                    width: 32,
                                    height: 2,
                                    background: "var(--accent)",
                                    display: "block",
                                }}
                            />
                            <span style={{ ...S.label, marginBottom: 0 }}>
                                Annecy · Silicon Alps · Agents IA
                            </span>
                        </div>
                        <h1 style={{ ...S.h1, marginBottom: 32 }}>
                            Agents IA &<br />
                            <span style={{ color: "var(--accent)" }}>
                                applications web
                            </span>
                            <br />
                            sur-mesure
                        </h1>
                        <p
                            style={{
                                ...S.body,
                                maxWidth: 580,
                                fontSize: 18,
                                marginBottom: 48,
                                lineHeight: 1.65,
                            }}
                        >
                            Alpes Logiciels conçoit des applications métier et intègre
                            l’intelligence artificielle dans vos processus.
                            Automations, agents IA, développement full-stack —
                            pour les entreprises de Haute-Savoie.
                        </p>
                        <div
                            style={{
                                display: "flex",
                                gap: 16,
                                flexWrap: "wrap",
                            }}
                        >
                            <button
                                onClick={() => {
                                    setPage("contact");
                                    window.scrollTo(0, 0);
                                }}
                                style={{
                                    padding: "16px 36px",
                                    borderRadius: 100,
                                    background: "var(--accent)",
                                    border: "none",
                                    color: "white",
                                    fontFamily: "var(--font-body)",
                                    fontSize: 16,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition:
                                        "transform 0.2s, box-shadow 0.2s",
                                    boxShadow:
                                        "0 0 40px oklch(62% 0.22 268 / 0.4)",
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform =
                                        "translateY(-2px)";
                                    e.currentTarget.style.boxShadow =
                                        "0 8px 50px oklch(62% 0.22 268 / 0.5)";
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = "none";
                                    e.currentTarget.style.boxShadow =
                                        "0 0 40px oklch(62% 0.22 268 / 0.4)";
                                }}
                            >
                                Obtenir un Audit IA Gratuit
                            </button>
                            <button
                                onClick={() => {
                                    setPage("expertises");
                                    window.scrollTo(0, 0);
                                }}
                                style={{
                                    padding: "16px 36px",
                                    borderRadius: 100,
                                    background: "transparent",
                                    border: "1px solid var(--border-dark)",
                                    color: "var(--text-dark)",
                                    fontFamily: "var(--font-body)",
                                    fontSize: 16,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "background 0.2s",
                                }}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.background =
                                        "var(--bg-card)")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.background =
                                        "transparent")
                                }
                            >
                                Nos Expertises →
                            </button>
                        </div>
                    </div>

                    {/* Stats row */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3,1fr)",
                            gap: 1,
                            marginTop: 100,
                            maxWidth: 600,
                            borderTop: "1px solid var(--border-dark)",
                            paddingTop: 48,
                        }}
                    >
                        {[
                            ["50+", "Projets livrés"],
                            ["6×", "ROI moyen constaté"],
                            ["2019", "Fondée à Annecy"],
                        ].map(([n, l]) => (
                            <div key={n}>
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontSize: 36,
                                        fontWeight: 800,
                                        color: "var(--text-dark)",
                                    }}
                                >
                                    {n}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "var(--text-muted-dark)",
                                        marginTop: 4,
                                    }}
                                >
                                    {l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY LOCAL */}
            <section
                style={{
                    ...S.section,
                    background: "var(--bg-card)",
                    borderTop: "1px solid var(--border-dark)",
                    borderBottom: "1px solid var(--border-dark)",
                }}
            >
                <div
                    style={{
                        ...S.container,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 80,
                        alignItems: "center",
                    }}
                >
                    <div>
                        <span style={S.label}>Proximité & Expertise</span>
                        <h2 style={{ ...S.h2, marginBottom: 24 }}>
                            Pourquoi choisir une agence web locale à Annecy ?
                        </h2>
                        <p style={{ ...S.body, marginBottom: 20 }}>
                            Située au cœur de la{" "}
                            <em
                                style={{
                                    color: "var(--text-dark)",
                                    fontStyle: "normal",
                                }}
                            >
                                «Silicon Alps»
                            </em>
                            , notre agence développe des applications web et des
                            solutions IA pour les PME, startups et industries du
                            bassin annécien.
                        </p>
                        <p style={{ ...S.body }}>
                            Contrairement aux agences généralistes, nous
                            combinons{" "}
                            <strong style={{ color: "var(--text-dark)" }}>
                                développement applicatif sur-mesure
                            </strong>{" "}
                            et intégration d'IA — pour des outils métier qui
                            s'adaptent vraiment à votre façon de travailler.
                        </p>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                        }}
                    >
                        {[
                            [
                                "📍",
                                "Ancrage local",
                                "Annecy-le-Vieux, Seynod, Meythet, Cran-Gevrier — nous connaissons votre territoire.",
                            ],
                            [
                                "⚡",
                                "Réactivité",
                                "Réunion dans nos bureaux ou chez vous sous 48h. Pas de ticket, un interlocuteur dédié.",
                            ],
                            [
                                "🎯",
                                "Double expertise",
                                "Développement web et IA dans la même équipe — pas deux prestataires à coordonner.",
                            ],
                        ].map(([icon, title, desc]) => (
                            <div
                                key={title}
                                style={{
                                    display: "flex",
                                    gap: 16,
                                    padding: "20px 24px",
                                    borderRadius: 14,
                                    border: "1px solid var(--border-dark)",
                                    background: "var(--bg-dark)",
                                    transition: "border-color 0.2s",
                                }}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.borderColor =
                                        "var(--accent)")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.borderColor =
                                        "var(--border-dark)")
                                }
                            >
                                <span style={{ fontSize: 22 }}>{icon}</span>
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 700,
                                            fontSize: 15,
                                            marginBottom: 4,
                                        }}
                                    >
                                        {title}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            lineHeight: 1.6,
                                            color: "var(--text-muted-dark)",
                                        }}
                                    >
                                        {desc}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* EXPERTISES */}
            <section style={S.section}>
                <div style={S.container}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                            marginBottom: 60,
                            flexWrap: "wrap",
                            gap: 20,
                        }}
                    >
                        <div>
                            <span style={S.label}>Ce que nous faisons</span>
                            <h2 style={S.h2}>
                                Nos Expertises
                                <br />
                                pour votre performance
                            </h2>
                        </div>
                        <button
                            onClick={() => {
                                setPage("expertises");
                                window.scrollTo(0, 0);
                            }}
                            style={{
                                background: "none",
                                border: "1px solid var(--border-dark)",
                                borderRadius: 100,
                                padding: "12px 28px",
                                color: "var(--text-dark)",
                                fontFamily: "var(--font-body)",
                                fontSize: 14,
                                cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.borderColor =
                                    "var(--accent)";
                                e.currentTarget.style.color = "var(--accent)";
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.borderColor =
                                    "var(--border-dark)";
                                e.currentTarget.style.color =
                                    "var(--text-dark)";
                            }}
                        >
                            Toutes nos expertises →
                        </button>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 20,
                        }}
                    >
                        {expertises.map((e) => (
                            <ExpertiseCard
                                key={e.num}
                                {...e}
                                onClick={() => {
                                    setPage(
                                        e.num === "02" ? "geo" : "expertises",
                                    );
                                    window.scrollTo(0, 0);
                                }}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* REALISATIONS */}
            <section style={S.section}>
                <div style={S.container}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                            marginBottom: 60,
                            flexWrap: "wrap",
                            gap: 20,
                        }}
                    >
                        <div>
                            <span style={S.label}>Cas clients</span>
                            <h2 style={S.h2}>Réalisations</h2>
                        </div>
                        <button
                            onClick={() => {
                                setPage("realisations");
                                window.scrollTo(0, 0);
                            }}
                            style={{
                                background: "none",
                                border: "1px solid var(--border-dark)",
                                borderRadius: 100,
                                padding: "12px 28px",
                                color: "var(--text-dark)",
                                fontFamily: "var(--font-body)",
                                fontSize: 14,
                                cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.borderColor =
                                    "var(--accent)";
                                e.currentTarget.style.color = "var(--accent)";
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.borderColor =
                                    "var(--border-dark)";
                                e.currentTarget.style.color =
                                    "var(--text-dark)";
                            }}
                        >
                            Voir tous les projets →
                        </button>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 20,
                        }}
                    >
                        <RealisationCard
                            tag="Site vitrine · IA · 2025"
                            title="Monde du Macaron"
                            desc="Site vitrine pour un salon de thé artisanal à Annecy — charte graphique, génération d’images IA et moteur de recherche sémantique."
                            color="#E8407A"
                            page="macaron"
                            logoSrc="/logo-macarons-shop.png"
                            externalUrl="https://macaronsshop.netlify.app/"
                            setPage={setPage}
                        />
                        <RealisationCard
                            tag="Logiciel SaaS · 6 ans · 2016–2022"
                            title="ACI-BIL Burdigala"
                            desc="Système de gestion SaaS sur-mesure pour une assurance santé internationale — 7 espaces métier, 47 M€ traités, déployé en Afrique et en Europe."
                            color="var(--accent)"
                            page="aci"
                            logoSrc="/logo-aci-burdigala.png"
                            externalUrl="https://www.aci-bil.fr"
                            setPage={setPage}
                        />
                    </div>
                </div>
            </section>

            {/* CTA BAND */}
            <section
                style={{
                    padding: "80px clamp(20px,6vw,80px)",
                    background: `linear-gradient(135deg, oklch(62% 0.22 268 / 0.15), oklch(68% 0.17 162 / 0.1))`,
                    borderTop: "1px solid var(--border-dark)",
                }}
            >
                <div style={{ ...S.container, textAlign: "center" }}>
                    <h2 style={{ ...S.h2, marginBottom: 20 }}>
                        Un projet web ou IA&nbsp;?
                    </h2>
                    <p
                        style={{
                            ...S.body,
                            maxWidth: 500,
                            margin: "0 auto 40px",
                            fontSize: 17,
                        }}
                    >
                        Audit offert — sans engagement. En 30 minutes, on cadre
                        votre besoin et vous proposons une approche concrète.
                    </p>
                    <button
                        onClick={() => {
                            setPage("contact");
                            window.scrollTo(0, 0);
                        }}
                        style={{
                            padding: "18px 48px",
                            borderRadius: 100,
                            background: "var(--accent)",
                            border: "none",
                            color: "white",
                            fontFamily: "var(--font-body)",
                            fontSize: 17,
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 0 60px oklch(62% 0.22 268 / 0.45)",
                            transition: "transform 0.2s",
                        }}
                        onMouseOver={(e) =>
                            (e.currentTarget.style.transform =
                                "translateY(-3px)")
                        }
                        onMouseOut={(e) =>
                            (e.currentTarget.style.transform = "none")
                        }
                    >
                        Demander mon Audit Gratuit
                    </button>
                </div>
            </section>
        </div>
    );
}

function ExpertiseCard({ num, title, sub, body, color, onClick }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onClick={onClick}
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            style={{
                padding: 32,
                borderRadius: 20,
                border: `1px solid ${hovered ? color : "var(--border-dark)"}`,
                background: hovered ? "var(--bg-card)" : "transparent",
                cursor: "pointer",
                transition: "all 0.25s",
                transform: hovered ? "translateY(-4px)" : "none",
            }}
        >
            <div
                style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 48,
                    fontWeight: 800,
                    color,
                    opacity: 0.25,
                    lineHeight: 1,
                    marginBottom: 20,
                }}
            >
                {num}
            </div>
            <div
                style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: 20,
                    marginBottom: 6,
                }}
            >
                {title}
            </div>
            <div
                style={{
                    fontSize: 12,
                    color: "var(--accent)",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 16,
                }}
            >
                {sub}
            </div>
            <p style={{ ...S.body, fontSize: 14, lineHeight: 1.7 }}>{body}</p>
            <div
                style={{ marginTop: 24, fontSize: 13, color, fontWeight: 600 }}
            >
                En savoir plus →
            </div>
        </div>
    );
}

// ————— PAGE: EXPERTISES —————
function PageExpertises({ setPage }) {
    const services = [
        {
            icon: "🖥️",
            title: "Applications Web Sur-Mesure",
            sub: "React · Next.js · Node.js · PostgreSQL",
            desc: "Nous concevons et développons des applications web métier adaptées à vos processus internes : CRM sur-mesure, plateformes SaaS, portails collaborateurs, outils de gestion. Chaque fonctionnalité est pensée pour vos utilisateurs réels.",
            points: [
                "Conception UX/UI orientée métier",
                "Développement full-stack moderne",
                "APIs REST & intégrations tierces",
                "Maintenance et évolution continue",
            ],
            color: "var(--accent)",
        },
        {
            icon: "🤖",
            title: "Intégration IA & Génération",
            sub: "OpenAI · Mistral · Anthropic · RAG",
            desc: "Nous enrichissons vos applications avec de l'intelligence artificielle : génération de contenu, extraction documentaire, chatbots métier, résumés automatiques et agents autonomes branchés sur vos données.",
            points: [
                "Chatbots et assistants contextualisés",
                "Génération & résumé de documents",
                "RAG sur vos bases de données internes",
                "Agents IA autonomes sur vos workflows",
            ],
            color: "var(--accent-2)",
        },
        {
            icon: "⚙️",
            title: "Automatisation de Processus",
            sub: "Make · Zapier · n8n · Webhooks",
            desc: "Du lead entrant à la facturation, chaque étape répétitive peut être orchestrée. Nous connectons vos outils existants et créons des workflows sur-mesure qui libèrent du temps à vos équipes.",
            points: [
                "Connexion CRM / Notion / Slack / Gmail",
                "Workflows multi-étapes et conditionnels",
                "Déclencheurs temps-réel et webhooks",
                "Monitoring et alertes automatisés",
            ],
            color: "var(--accent)",
        },
        {
            icon: "🔍",
            title: "GEO & Référencement IA",
            sub: "ChatGPT · Gemini · Perplexity · Claude",
            desc: "Vos futurs clients cherchent sur ChatGPT, pas seulement sur Google. Nous optimisons votre contenu et votre structure sémantique pour que votre marque soit citée comme référence par les modèles génératifs.",
            points: [
                "Audit de visibilité dans les LLM",
                "Structuration de données & schémas",
                "Contenu conversationnel IA-ready",
                "Autorité sémantique & stratégie éditoriale",
            ],
            color: "var(--accent-2)",
        },
    ];

    return (
        <div className="page-enter">
            <section style={{ ...S.section, paddingTop: 140 }}>
                <div style={S.container}>
                    <span style={S.label}>Ce que nous faisons</span>
                    <h1 style={{ ...S.h1, maxWidth: 700, marginBottom: 24 }}>
                        Nos Expertises
                    </h1>
                    <p style={{ ...S.body, maxWidth: 580, fontSize: 18 }}>
                        Développement web sur-mesure et intégration IA — quatre
                        expertises complémentaires pour des outils qui font
                        vraiment la différence.
                    </p>
                </div>
            </section>

            <section style={{ padding: "0 clamp(20px,6vw,80px) 100px" }}>
                <div
                    style={{
                        ...S.container,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {services.map((s, i) => (
                        <ServiceRow
                            key={s.title}
                            {...s}
                            reverse={i % 2 === 1}
                            onGeoClick={null}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}

function ServiceRow({
    icon,
    title,
    sub,
    desc,
    points,
    color,
    reverse,
    onGeoClick,
}) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            onClick={onGeoClick}
            style={{
                cursor: onGeoClick ? "pointer" : "default",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 60,
                alignItems: "center",
                padding: "60px 0",
                borderBottom: "1px solid var(--border-dark)",
                direction: reverse ? "rtl" : "ltr",
            }}
        >
            <div style={{ direction: "ltr" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 20,
                    }}
                >
                    <span style={{ fontSize: 28 }}>{icon}</span>
                    <span
                        style={{
                            fontFamily: "var(--font-body)",
                            fontSize: 12,
                            color,
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                        }}
                    >
                        {sub}
                    </span>
                </div>
                <h3 style={{ ...S.h3, fontSize: 32, marginBottom: 16 }}>
                    {title}
                </h3>
                <p style={{ ...S.body, marginBottom: 28 }}>{desc}</p>
                {onGeoClick && (
                    <div style={{ fontSize: 14, color, fontWeight: 600 }}>
                        Voir notre méthodologie GEO →
                    </div>
                )}
            </div>
            <div
                style={{
                    direction: "ltr",
                    background: "var(--bg-card)",
                    borderRadius: 20,
                    padding: 36,
                    border: `1px solid ${hovered ? color : "var(--border-dark)"}`,
                    transition: "border-color 0.3s",
                }}
            >
                <div
                    style={{
                        fontSize: 12,
                        color: "var(--text-muted-dark)",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        marginBottom: 20,
                    }}
                >
                    Inclus dans ce service
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                    }}
                >
                    {points.map((p) => (
                        <div
                            key={p}
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 12,
                            }}
                        >
                            <div
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: 6,
                                    background: `${color}22`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    marginTop: 2,
                                }}
                            >
                                <svg
                                    width="10"
                                    height="8"
                                    viewBox="0 0 10 8"
                                    fill="none"
                                >
                                    <path
                                        d="M1 4L3.5 6.5L9 1"
                                        stroke={color}
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <span style={{ fontSize: 15, lineHeight: 1.55 }}>
                                {p}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ————— PAGE: GEO —————
function PageGEO({ setPage }) {
    const steps = [
        {
            num: "01",
            title: "Audit de Visibilité IA",
            desc: "Nous interrogeons systématiquement GPT-4, Claude, Gemini et Perplexity sur vos requêtes cibles. Votre marque est-elle citée ? Avec quelle précision ? Quel concurrent vous devance ?",
            color: "var(--accent)",
        },
        {
            num: "02",
            title: "Structuration de Données",
            desc: "Mise en place de schémas JSON-LD complexes, de balisage sémantique avancé et d'une architecture d'information que les LLM peuvent extraire et comprendre sans ambiguïté.",
            color: "var(--accent-2)",
        },
        {
            num: "03",
            title: "Contenu Conversationnel",
            desc: 'Rédaction de pages "IA-ready" qui répondent directement aux intentions de recherche sous forme de questions/réponses, définitions et synthèses que les modèles adorent citer.',
            color: "var(--accent)",
        },
        {
            num: "04",
            title: "Autorité Sémantique",
            desc: "Renforcement de votre écosystème digital : mentions sur des sources authoritative, co-citations sectorielles, et stratégie éditoriale long-terme pour devenir incontournable dans votre domaine.",
            color: "var(--accent-2)",
        },
    ];

    return (
        <div className="page-enter">
            {/* Hero */}
            <section
                style={{
                    ...S.section,
                    paddingTop: 140,
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Orbs />
                <div
                    style={{ ...S.container, position: "relative", zIndex: 1 }}
                >
                    <span style={S.label}>
                        GEO — Generative Engine Optimization
                    </span>
                    <h1 style={{ ...S.h1, maxWidth: 800, marginBottom: 28 }}>
                        Soyez la réponse
                        <br />
                        choisie par{" "}
                        <span style={{ color: "var(--accent)" }}>ChatGPT</span>
                        <br />
                        et{" "}
                        <span style={{ color: "var(--accent-2)" }}>Gemini</span>
                    </h1>
                    <p
                        style={{
                            ...S.body,
                            maxWidth: 560,
                            fontSize: 18,
                            lineHeight: 1.65,
                        }}
                    >
                        À Annecy, la compétition digitale évolue. Les clients
                        posent des questions à l'IA. Notre agence maîtrise le
                        GEO pour garantir que votre marque soit citée comme la
                        référence locale par les intelligences artificielles
                        génératives.
                    </p>
                </div>
            </section>

            {/* What is GEO */}
            <section
                style={{
                    ...S.section,
                    background: "var(--bg-card)",
                    borderTop: "1px solid var(--border-dark)",
                    borderBottom: "1px solid var(--border-dark)",
                }}
            >
                <div
                    style={{
                        ...S.container,
                        display: "grid",
                        gridTemplateColumns: "5fr 7fr",
                        gap: 80,
                        alignItems: "center",
                    }}
                >
                    <div>
                        <span style={S.label}>Définition</span>
                        <h2 style={{ ...S.h2, fontSize: 38, marginBottom: 24 }}>
                            Qu'est-ce que le GEO ?
                        </h2>
                        <p style={{ ...S.body, marginBottom: 18 }}>
                            Le{" "}
                            <strong style={{ color: "var(--text-dark)" }}>
                                Generative Engine Optimization
                            </strong>{" "}
                            est l'ensemble des techniques permettant à votre
                            marque d'apparaître comme source de référence dans
                            les réponses générées par des modèles IA.
                        </p>
                        <p style={{ ...S.body }}>
                            Contrairement au SEO classique (positionnement sur
                            Google), le GEO s'attaque aux citations dans les
                            interfaces conversationnelles : ChatGPT, Claude,
                            Gemini, Perplexity — là où se déplace l'attention de
                            vos futurs clients.
                        </p>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 16,
                        }}
                    >
                        {[
                            [
                                "68%",
                                "des utilisateurs font davantage confiance aux réponses IA qu'aux résultats de recherche traditionnels",
                            ],
                            [
                                "3×",
                                "plus de conversions sur les leads issus d'une recommandation IA vs. un clic organique",
                            ],
                            [
                                "2026",
                                "année où les recherches IA devraient dépasser les recherches Google selon les analystes",
                            ],
                            [
                                "Top 3",
                                "positions cibles dans les réponses LLM — au-delà, votre marque n'est jamais mentionnée",
                            ],
                        ].map(([stat, desc]) => (
                            <div
                                key={stat}
                                style={{
                                    padding: 24,
                                    borderRadius: 16,
                                    border: "1px solid var(--border-dark)",
                                    background: "var(--bg-dark)",
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontSize: 32,
                                        fontWeight: 800,
                                        color: "var(--accent)",
                                        marginBottom: 8,
                                    }}
                                >
                                    {stat}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                        color: "var(--text-muted-dark)",
                                    }}
                                >
                                    {desc}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 4 Steps */}
            <section style={S.section}>
                <div style={S.container}>
                    <div style={{ textAlign: "center", marginBottom: 70 }}>
                        <span style={S.label}>Notre Méthodologie</span>
                        <h2 style={{ ...S.h2, margin: "0 auto" }}>
                            Notre approche en 4 étapes
                        </h2>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2,1fr)",
                            gap: 24,
                        }}
                    >
                        {steps.map((step) => (
                            <StepCard key={step.num} {...step} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section
                style={{
                    padding: "80px clamp(20px,6vw,80px)",
                    borderTop: "1px solid var(--border-dark)",
                    background: "var(--bg-card)",
                }}
            >
                <div
                    style={{
                        ...S.container,
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 40,
                        alignItems: "center",
                    }}
                >
                    <div>
                        <h2 style={{ ...S.h2, fontSize: 36, marginBottom: 12 }}>
                            Audit GEO Gratuit pour votre entreprise
                        </h2>
                        <p style={{ ...S.body }}>
                            En 30 minutes, on vous dit exactement comment
                            ChatGPT et Gemini vous perçoivent aujourd'hui — et
                            ce qu'il faut corriger.
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setPage("contact");
                            window.scrollTo(0, 0);
                        }}
                        style={{
                            padding: "18px 40px",
                            borderRadius: 100,
                            background: "var(--accent)",
                            border: "none",
                            color: "white",
                            fontFamily: "var(--font-body)",
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            transition: "transform 0.2s",
                        }}
                        onMouseOver={(e) =>
                            (e.currentTarget.style.transform =
                                "translateY(-2px)")
                        }
                        onMouseOut={(e) =>
                            (e.currentTarget.style.transform = "none")
                        }
                    >
                        Demander mon Audit →
                    </button>
                </div>
            </section>
        </div>
    );
}

function StepCard({ num, title, desc, color }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            style={{
                padding: 40,
                borderRadius: 20,
                border: `1px solid ${hovered ? color : "var(--border-dark)"}`,
                background: hovered ? "var(--bg-card)" : "transparent",
                transition: "all 0.25s",
                transform: hovered ? "translateY(-4px)" : "none",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    marginBottom: 24,
                }}
            >
                <div
                    style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: `${color}1a`,
                        border: `1px solid ${color}44`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <span
                        style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 800,
                            fontSize: 14,
                            color,
                        }}
                    >
                        {num}
                    </span>
                </div>
                <h3
                    style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 700,
                        fontSize: 20,
                    }}
                >
                    {title}
                </h3>
            </div>
            <p style={{ ...S.body, fontSize: 15 }}>{desc}</p>
        </div>
    );
}

// ————— PAGE: FORMATION —————
function PageFormation({ setPage }) {
    const workshops = [
        {
            title: "Prompt Engineering Avancé",
            duration: "1 jour",
            level: "Tous niveaux",
            desc: "Maîtrisez l'art d'instruire les LLMs pour obtenir des résultats précis, reproductibles et adaptés à votre métier.",
        },
        {
            title: "IA Générative & Images",
            duration: "½ journée",
            level: "Débutant",
            desc: "Midjourney, DALL·E, Stable Diffusion — créez vos visuels professionnels et intégrez la génération d'images dans vos workflows créatifs.",
        },
        {
            title: "Automatisation sans code",
            duration: "2 jours",
            level: "Intermédiaire",
            desc: "Make (ex-Integromat) et Zapier de A à Z. Construisez vos premiers automatismes en atelier pratique sur vos vrais cas d'usage.",
        },
        {
            title: "Stratégie IA pour Dirigeants",
            duration: "½ journée",
            level: "Décideurs",
            desc: "Cartographier les opportunités IA de votre secteur, évaluer le ROI, piloter une équipe IA — la formation stratégique pour les décideurs.",
        },
    ];

    return (
        <div className="page-enter">
            <section style={{ ...S.section, paddingTop: 140 }}>
                <div style={S.container}>
                    <span style={S.label}>Formation & Accompagnement</span>
                    <h1 style={{ ...S.h1, maxWidth: 760, marginBottom: 28 }}>
                        Montez en compétences
                        <br />
                        avec vos équipes
                    </h1>
                    <p style={{ ...S.body, maxWidth: 560, fontSize: 18 }}>
                        Ateliers pratiques dans nos bureaux à Annecy ou dans vos
                        locaux. Des formations concrètes, ancrées dans vos vrais
                        outils et processus.
                    </p>
                </div>
            </section>

            <section style={{ padding: "0 clamp(20px,6vw,80px) 100px" }}>
                <div style={{ ...S.container }}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2,1fr)",
                            gap: 20,
                            marginBottom: 60,
                        }}
                    >
                        {workshops.map((w) => (
                            <WorkshopCard key={w.title} {...w} />
                        ))}
                    </div>

                    {/* Format info */}
                    <div
                        style={{
                            background: "var(--bg-card)",
                            borderRadius: 24,
                            padding: 48,
                            border: "1px solid var(--border-dark)",
                            display: "grid",
                            gridTemplateColumns: "repeat(3,1fr)",
                            gap: 40,
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 800,
                                    fontSize: 20,
                                    marginBottom: 10,
                                }}
                            >
                                Formats disponibles
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                }}
                            >
                                {[
                                    "Présentiel Annecy",
                                    "Dans vos locaux",
                                    "Visioconférence",
                                    "Format hybride",
                                ].map((f) => (
                                    <div
                                        key={f}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            fontSize: 14,
                                            color: "var(--text-muted-dark)",
                                        }}
                                    >
                                        <span
                                            style={{ color: "var(--accent-2)" }}
                                        >
                                            ✓
                                        </span>{" "}
                                        {f}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div
                                style={{
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 800,
                                    fontSize: 20,
                                    marginBottom: 10,
                                }}
                            >
                                Public cible
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                }}
                            >
                                {[
                                    "Équipes marketing",
                                    "Développeurs",
                                    "Dirigeants PME",
                                    "RH & Ops",
                                ].map((f) => (
                                    <div
                                        key={f}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            fontSize: 14,
                                            color: "var(--text-muted-dark)",
                                        }}
                                    >
                                        <span
                                            style={{ color: "var(--accent)" }}
                                        >
                                            →
                                        </span>{" "}
                                        {f}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div
                                style={{
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 800,
                                    fontSize: 20,
                                    marginBottom: 16,
                                }}
                            >
                                Sur devis
                            </div>
                            <p
                                style={{
                                    fontSize: 14,
                                    color: "var(--text-muted-dark)",
                                    lineHeight: 1.65,
                                    marginBottom: 20,
                                }}
                            >
                                Toutes nos formations sont personnalisées selon
                                votre secteur et vos outils existants.
                            </p>
                            <button
                                onClick={() => {
                                    setPage("contact");
                                    window.scrollTo(0, 0);
                                }}
                                style={{
                                    padding: "12px 28px",
                                    borderRadius: 100,
                                    background: "var(--accent)",
                                    border: "none",
                                    color: "white",
                                    fontFamily: "var(--font-body)",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                Demander un devis
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function WorkshopCard({ title, duration, level, desc }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            style={{
                padding: 36,
                borderRadius: 20,
                border: `1px solid ${hovered ? "var(--accent)" : "var(--border-dark)"}`,
                background: hovered ? "var(--bg-card)" : "transparent",
                transition: "all 0.25s",
                transform: hovered ? "translateY(-4px)" : "none",
            }}
        >
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--accent)",
                        background: "var(--accent)1a",
                        padding: "4px 12px",
                        borderRadius: 100,
                    }}
                >
                    {level}
                </span>
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--text-muted-dark)",
                        background: "var(--border-dark)",
                        padding: "4px 12px",
                        borderRadius: 100,
                    }}
                >
                    {duration}
                </span>
            </div>
            <h3 style={{ ...S.h3, fontSize: 22, marginBottom: 14 }}>{title}</h3>
            <p style={{ ...S.body, fontSize: 14 }}>{desc}</p>
        </div>
    );
}

// ————— PAGE: CONTACT —————
function PageContact() {
    const CONTACT_ENDPOINT = "https://www.ia-annecy.fr/api/chat/contact";
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: "",
        message: "",
    });
    const [sent, setSent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitError("");
        setIsSubmitting(true);

        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                message: form.message.trim(),
                companyName: form.company.trim(),
            };

            const response = await fetch(CONTACT_ENDPOINT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Contact API error: ${response.status}`);
            }

            setSent(true);
        } catch (error) {
            setSubmitError(
                "Impossible d'envoyer le formulaire pour le moment. Réessaie dans quelques instants.",
            );
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <div className="page-enter">
            <section style={{ ...S.section, paddingTop: 140 }}>
                <div
                    style={{
                        ...S.container,
                        display: "grid",
                        gridTemplateColumns: "5fr 7fr",
                        gap: 80,
                    }}
                >
                    <div>
                        <span style={S.label}>Parlons-nous</span>
                        <h1
                            style={{
                                ...S.h1,
                                fontSize: "clamp(40px,4.5vw,66px)",
                                marginBottom: 28,
                            }}
                        >
                            Démarrons
                            <br />
                            votre projet
                        </h1>
                        <p
                            style={{
                                ...S.body,
                                fontSize: 17,
                                marginBottom: 48,
                            }}
                        >
                            Audit gratuit, projet IA ou formation — décrivez
                            votre besoin et nous vous répondons sous 24h.
                        </p>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 24,
                            }}
                        >
                            {[
                                [
                                    "📍",
                                    "Adresse",
                                    "19 chemin de charnay, 74940 Annecy-le-Vieux",
                                ],
                                ["📞", "Téléphone", "+33 6 26 52 18 44"],
                                ["✉️", "Email", "mickael@alpes-logiciels.fr"],
                            ].map(([icon, label, val]) => (
                                <div
                                    key={label}
                                    style={{
                                        display: "flex",
                                        gap: 16,
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <span style={{ fontSize: 20 }}>{icon}</span>
                                    <div>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "var(--text-muted-dark)",
                                                fontWeight: 600,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.08em",
                                                marginBottom: 2,
                                            }}
                                        >
                                            {label}
                                        </div>
                                        <div style={{ fontSize: 15 }}>
                                            {val}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div
                            style={{
                                marginTop: 48,
                                padding: "24px",
                                borderRadius: 16,
                                border: "1px solid var(--border-dark)",
                                background: "var(--bg-card)",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    color: "var(--accent)",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginBottom: 10,
                                }}
                            >
                                Zone d'intervention
                            </div>
                            <div
                                style={{
                                    fontSize: 13,
                                    color: "var(--text-muted-dark)",
                                    lineHeight: 1.8,
                                }}
                            >
                                Annecy · Annecy-le-Vieux · Pringy · Epagny
                                <br />
                                Rumilly · Thônes · Seynod · Genève
                            </div>
                        </div>
                    </div>

                    <div>
                        {sent ? (
                            <div
                                style={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    padding: 60,
                                }}
                            >
                                <div
                                    style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        background: "var(--accent-2)1a",
                                        border: "1px solid var(--accent-2)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 24,
                                    }}
                                >
                                    <svg
                                        width="28"
                                        height="22"
                                        viewBox="0 0 28 22"
                                        fill="none"
                                    >
                                        <path
                                            d="M2 11L10 19L26 3"
                                            stroke="var(--accent-2)"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                                <h3 style={{ ...S.h3, marginBottom: 12 }}>
                                    Message envoyé !
                                </h3>
                                <p style={{ ...S.body }}>
                                    Nous vous répondons dans les 24 heures.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 16,
                                }}
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 16,
                                    }}
                                >
                                    <FormField
                                        label="Prénom & Nom"
                                        value={form.name}
                                        onChange={(v) =>
                                            setForm({ ...form, name: v })
                                        }
                                        required
                                    />
                                    <FormField
                                        label="Entreprise"
                                        value={form.company}
                                        onChange={(v) =>
                                            setForm({ ...form, company: v })
                                        }
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 16,
                                    }}
                                >
                                    <FormField
                                        label="Email"
                                        type="email"
                                        value={form.email}
                                        onChange={(v) =>
                                            setForm({ ...form, email: v })
                                        }
                                        required
                                    />
                                    <FormField
                                        label="Téléphone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={(v) =>
                                            setForm({ ...form, phone: v })
                                        }
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 6,
                                    }}
                                >
                                    <label
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted-dark)",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                        }}
                                    >
                                        Service souhaité
                                    </label>
                                    <select
                                        value={form.service}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                service: e.target.value,
                                            })
                                        }
                                        style={{
                                            background: "var(--bg-card)",
                                            border: "1px solid var(--border-dark)",
                                            borderRadius: 12,
                                            color: form.service
                                                ? "var(--text-dark)"
                                                : "var(--text-muted-dark)",
                                            fontFamily: "var(--font-body)",
                                            fontSize: 15,
                                            padding: "14px 16px",
                                            outline: "none",
                                        }}
                                    >
                                        <option value="">
                                            Sélectionnez...
                                        </option>
                                        <option>Audit IA Gratuit</option>
                                        <option>
                                            Automatisation de Processus
                                        </option>
                                        <option>GEO & Référencement IA</option>
                                        <option>
                                            Développement IA Sur-Mesure
                                        </option>
                                        <option>Formation IA</option>
                                        <option>Autre</option>
                                    </select>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 6,
                                    }}
                                >
                                    <label
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted-dark)",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                        }}
                                    >
                                        Votre projet
                                    </label>
                                    <textarea
                                        value={form.message}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                message: e.target.value,
                                            })
                                        }
                                        rows={5}
                                        placeholder="Décrivez votre besoin ou posez vos questions..."
                                        style={{
                                            background: "var(--bg-card)",
                                            border: "1px solid var(--border-dark)",
                                            borderRadius: 12,
                                            color: "var(--text-dark)",
                                            fontFamily: "var(--font-body)",
                                            fontSize: 15,
                                            padding: "14px 16px",
                                            outline: "none",
                                            resize: "vertical",
                                        }}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    style={{
                                        padding: "16px",
                                        borderRadius: 12,
                                        background: "var(--accent)",
                                        border: "none",
                                        color: "white",
                                        fontFamily: "var(--font-body)",
                                        fontSize: 16,
                                        fontWeight: 600,
                                        cursor: isSubmitting
                                            ? "not-allowed"
                                            : "pointer",
                                        opacity: isSubmitting ? 0.7 : 1,
                                        transition:
                                            "transform 0.2s, box-shadow 0.2s",
                                        boxShadow:
                                            "0 0 40px oklch(62% 0.22 268 / 0.3)",
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform =
                                            "translateY(-2px)";
                                        e.currentTarget.style.boxShadow =
                                            "0 8px 40px oklch(62% 0.22 268 / 0.5)";
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform =
                                            "none";
                                        e.currentTarget.style.boxShadow =
                                            "0 0 40px oklch(62% 0.22 268 / 0.3)";
                                    }}
                                >
                                    {isSubmitting
                                        ? "Envoi en cours..."
                                        : "Envoyer ma demande"}
                                </button>
                                {submitError ? (
                                    <p
                                        style={{
                                            fontSize: 13,
                                            color: "#ff8b8b",
                                            textAlign: "center",
                                        }}
                                    >
                                        {submitError}
                                    </p>
                                ) : null}
                                <p
                                    style={{
                                        fontSize: 12,
                                        color: "var(--text-muted-dark)",
                                        textAlign: "center",
                                    }}
                                >
                                    Réponse garantie sous 24h · Audit gratuit
                                    sans engagement
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

function FormField({ label, type = "text", value, onChange, required }) {
    const [focused, setFocused] = useState(false);
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label
                style={{
                    fontSize: 12,
                    color: "var(--text-muted-dark)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                }}
            >
                {label}
                {required && " *"}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={{
                    background: "var(--bg-card)",
                    border: `1px solid ${focused ? "var(--accent)" : "var(--border-dark)"}`,
                    borderRadius: 12,
                    color: "var(--text-dark)",
                    fontFamily: "var(--font-body)",
                    fontSize: 15,
                    padding: "14px 16px",
                    outline: "none",
                    transition: "border-color 0.2s",
                }}
            />
        </div>
    );
}

// ————— PAGE: MENTIONS LEGALES —————
function PageMentionsLegales() {
    const blockTitleStyle = {
        ...S.h3,
        fontSize: 24,
        marginBottom: 12,
    };

    const blockTextStyle = {
        ...S.body,
        fontSize: 15,
    };

    return (
        <div className="page-enter">
            <section style={{ ...S.section, paddingTop: 140 }}>
                <div style={S.container}>
                    <span style={S.label}>Informations légales</span>
                    <h1 style={{ ...S.h1, maxWidth: 900, marginBottom: 24 }}>
                        Mentions légales
                    </h1>
                    <p style={{ ...S.body, maxWidth: 760, marginBottom: 50 }}>
                        Cette page présente les informations légales de
                        l'éditeur du site Alpes Logiciels. Certaines données sont
                        provisoires et seront mises à jour après validation.
                    </p>

                    <div
                        style={{
                            display: "grid",
                            gap: 18,
                        }}
                    >
                        <div
                            style={{
                                border: "1px solid var(--border-dark)",
                                borderRadius: 16,
                                padding: 28,
                                background: "var(--bg-card)",
                            }}
                        >
                            <h2 style={blockTitleStyle}>Éditeur du site</h2>
                            <p style={blockTextStyle}>
                                Alpes Logiciels
                                <br />
                                19 chemin de charnay, 74940 Annecy-le-Vieux
                                <br />
                                Téléphone : +33 6 26 52 18 44
                                <br />
                                Email : mickael@alpes-logiciels.fr
                            </p>
                        </div>

                        <div
                            style={{
                                border: "1px solid var(--border-dark)",
                                borderRadius: 16,
                                padding: 28,
                                background: "var(--bg-card)",
                            }}
                        >
                            <h2 style={blockTitleStyle}>
                                Responsable de la publication
                            </h2>
                            <p style={blockTextStyle}>
                                À compléter (nom, prénom, qualité).
                            </p>
                        </div>

                        <div
                            style={{
                                border: "1px solid var(--border-dark)",
                                borderRadius: 16,
                                padding: 28,
                                background: "var(--bg-card)",
                            }}
                        >
                            <h2 style={blockTitleStyle}>Hébergement</h2>
                            <p style={blockTextStyle}>
                                À compléter (nom de l'hébergeur, adresse postale
                                complète, contact).
                            </p>
                        </div>

                        <div
                            style={{
                                border: "1px solid var(--border-dark)",
                                borderRadius: 16,
                                padding: 28,
                                background: "var(--bg-card)",
                            }}
                        >
                            <h2 style={blockTitleStyle}>
                                Propriété intellectuelle
                            </h2>
                            <p style={blockTextStyle}>
                                L'ensemble des contenus présents sur ce site
                                (textes, visuels, logos, éléments graphiques et
                                structure) est protégé par le droit de la
                                propriété intellectuelle. Toute reproduction ou
                                représentation, totale ou partielle, sans
                                autorisation préalable est interdite.
                            </p>
                        </div>

                        <div
                            style={{
                                border: "1px solid var(--border-dark)",
                                borderRadius: 16,
                                padding: 28,
                                background: "var(--bg-card)",
                            }}
                        >
                            <h2 style={blockTitleStyle}>
                                Données personnelles
                            </h2>
                            <p style={blockTextStyle}>
                                Les informations recueillies via le formulaire de
                                contact sont utilisées uniquement pour répondre
                                aux demandes reçues. La base légale, la durée de
                                conservation et la procédure d'exercice des
                                droits (accès, rectification, suppression,
                                opposition) seront précisées après validation.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

// ————— FOOTER —————
function Footer({ setPage }) {
    const links = [
        { id: "home", label: "Accueil" },
        { id: "expertises", label: "Expertises" },
    ];
    return (
        <footer
            style={{
                background: "var(--bg-card)",
                borderTop: "1px solid var(--border-dark)",
                padding: "60px clamp(20px,6vw,80px) 32px",
            }}
        >
            <div style={{ ...S.container }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr",
                        gap: 48,
                        marginBottom: 60,
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginBottom: 20,
                            }}
                        >
                            <span
                                style={{
                                    fontFamily: "var(--font-logo)",
                                    fontWeight: 800,
                                    fontSize: 30,
                                    letterSpacing: "1px",
                                }}
                            >
                                Alpes Logiciels
                            </span>
                        </div>
                        <p
                            style={{
                                fontSize: 14,
                                color: "var(--text-muted-dark)",
                                lineHeight: 1.75,
                                marginBottom: 16,
                            }}
                        >
                            Agence web spécialisée en applications métier et IA
                            à Annecy. Automatisation, GEO et développement
                            sur-mesure pour les entreprises de Haute-Savoie.
                        </p>
                        <div
                            style={{
                                fontSize: 13,
                                color: "var(--text-muted-dark)",
                            }}
                        >
                            19 chemin de charnay, 74940 Annecy-le-Vieux
                            <br />
                            +33 6 26 52 18 44
                            <br />
                            mickael@alpes-logiciels.fr
                        </div>
                    </div>
                    <div>
                        <div
                            style={{
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: 14,
                                marginBottom: 16,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Navigation
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                            }}
                        >
                            {links.map((l) => (
                                <button
                                    key={l.id}
                                    onClick={() => {
                                        setPage(l.id);
                                        window.scrollTo(0, 0);
                                    }}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "var(--text-muted-dark)",
                                        fontFamily: "var(--font-body)",
                                        fontSize: 14,
                                        textAlign: "left",
                                        transition: "color 0.2s",
                                        padding: 0,
                                    }}
                                    onMouseOver={(e) =>
                                        (e.target.style.color =
                                            "var(--text-dark)")
                                    }
                                    onMouseOut={(e) =>
                                        (e.target.style.color =
                                            "var(--text-muted-dark)")
                                    }
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div
                            style={{
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: 14,
                                marginBottom: 16,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Services
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                            }}
                        >
                            {[
                                "Applications Web",
                                "Intégration IA",
                                "Automatisation",
                                "GEO & SEO IA",
                                "Formation",
                            ].map((s) => (
                                <span
                                    key={s}
                                    style={{
                                        color: "var(--text-muted-dark)",
                                        fontSize: 14,
                                    }}
                                >
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div
                            style={{
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: 14,
                                marginBottom: 16,
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                            }}
                        >
                            Zone d'intervention
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                        >
                            {[
                                "Annecy",
                                "Annecy-le-Vieux",
                                "Pringy",
                                "Epagny",
                                "Rumilly",
                                "Thônes",
                                "Genève",
                            ].map((z) => (
                                <span
                                    key={z}
                                    style={{
                                        color: "var(--text-muted-dark)",
                                        fontSize: 13,
                                    }}
                                >
                                    {z}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
                <div
                    style={{
                        borderTop: "1px solid var(--border-dark)",
                        paddingTop: 28,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 16,
                    }}
                >
                    <span
                        style={{
                            fontSize: 13,
                            color: "var(--text-muted-dark)",
                        }}
                    >
                        © 2026 Agence IA Annecy. Tous droits réservés.
                    </span>
                    <div style={{ display: "flex", gap: 24 }}>
                        <button
                            onClick={() => {
                                setPage("mentions-legales");
                                window.scrollTo(0, 0);
                            }}
                            style={{
                                fontSize: 12,
                                color: "var(--text-muted-dark)",
                                cursor: "pointer",
                                background: "none",
                                border: "none",
                                padding: 0,
                            }}
                        >
                            Mentions légales
                        </button>
                        {["Politique de confidentialité", "Plan du site"].map(
                            (l) => (
                                <span
                                    key={l}
                                    style={{
                                        fontSize: 12,
                                        color: "var(--text-muted-dark)",
                                        cursor: "pointer",
                                    }}
                                >
                                    {l}
                                </span>
                            ),
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
}

// ————— REALISATION CARD —————
function RealisationCard({
    tag,
    title,
    desc,
    color,
    page,
    logoSrc,
    externalUrl,
    setPage,
}) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onClick={() => {
                setPage(page);
                window.scrollTo(0, 0);
            }}
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            style={{
                cursor: "pointer",
                borderRadius: 20,
                border: `1px solid ${hovered ? color : "var(--border-dark)"}`,
                background: hovered ? "var(--bg-card)" : "transparent",
                transition: "all 0.25s",
                transform: hovered ? "translateY(-4px)" : "none",
                overflow: "hidden",
            }}
        >
            {/* Color band */}
            <div style={{ height: 5, background: color }} />
            <div style={{ padding: 36 }}>
                {logoSrc ? (
                    <div
                        style={{
                            height: 96,
                            marginBottom: 22,
                            borderRadius: 12,
                            border: "1px solid var(--border-dark)",
                            background: "var(--bg-card)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "12px 16px",
                        }}
                    >
                        <img
                            src={logoSrc}
                            alt={`Logo ${title}`}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                width: "auto",
                                height: "auto",
                                display: "block",
                            }}
                        />
                    </div>
                ) : null}
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color,
                        marginBottom: 16,
                    }}
                >
                    {tag}
                </div>
                <h3 style={{ ...S.h3, fontSize: 26, marginBottom: 14 }}>
                    {title}
                </h3>
                <p style={{ ...S.body, fontSize: 14, lineHeight: 1.7 }}>
                    {desc}
                </p>
                <div
                    style={{
                        marginTop: 28,
                        fontSize: 13,
                        color,
                        fontWeight: 600,
                    }}
                >
                    Voir le projet →
                </div>
                {externalUrl ? (
                    <a
                        href={externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            marginTop: 12,
                            display: "inline-block",
                            fontSize: 13,
                            color: "var(--text-muted-dark)",
                            textDecoration: "underline",
                        }}
                    >
                        Voir le site live
                    </a>
                ) : null}
            </div>
        </div>
    );
}

// ————— PAGE: REALISATIONS —————
function PageRealisations({ setPage }) {
    const projets = [
        {
            tag: "Site vitrine · IA · 2025",
            title: "Monde du Macaron",
            desc: "Site vitrine pour un salon de thé artisanal à Annecy — charte graphique, génération d'images par IA et moteur de recherche sémantique augmenté.",
            color: "#E8407A",
            page: "macaron",
            logoSrc: "/logo-macarons-shop.png",
            externalUrl: "https://macaronsshop.netlify.app/",
        },
        {
            tag: "Logiciel SaaS · 6 ans · 2016–2022",
            title: "ACI-BIL Burdigala",
            desc: "Système de gestion SaaS sur-mesure pour une assurance santé internationale — 7 espaces métier, 47 M€ traités, déployé en Afrique et en Europe.",
            color: "var(--accent)",
            page: "aci",
            logoSrc: "/logo-aci-burdigala.png",
            externalUrl: "https://www.aci-bil.fr",
        },
    ];
    return (
        <div className="page-enter">
            <section style={{ ...S.section, paddingTop: 140 }}>
                <div style={S.container}>
                    <span style={S.label}>Cas clients</span>
                    <h1 style={{ ...S.h1, maxWidth: 700, marginBottom: 24 }}>
                        Réalisations
                    </h1>
                    <p style={{ ...S.body, maxWidth: 560, fontSize: 18 }}>
                        Applications métier, sites vitrines augmentés par l'IA,
                        logiciels SaaS sur-mesure — des projets concrets, livrés
                        et en production.
                    </p>
                </div>
            </section>
            <section style={{ padding: "0 clamp(20px,6vw,80px) 100px" }}>
                <div
                    style={{
                        ...S.container,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 24,
                    }}
                >
                    {projets.map((p) => (
                        <RealisationCard
                            key={p.page}
                            {...p}
                            setPage={setPage}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}

// ————— PAGE: MACARON —————
function PageMacaron({ setPage }) {
    const innovations = [
        {
            num: "01",
            title: "Charte Graphique & Identité",
            desc: "Logo, positionnement de marque et design system complet — palette Rose Macaron, Charbon, Crème Ivoire, typographies Playfair Display et DM Sans. Une identité qui respire l'élégance française et l'artisanat local.",
            color: "#E8407A",
        },
        {
            num: "02",
            title: "Génération d'Images par IA",
            desc: "Tous les visuels produit ont été créés via des modèles de diffusion (Midjourney / DALL·E) à partir de prompts soigneusement rédigés pour refléter l'identité de marque. Résultat : un catalogue visuel cohérent, sans shooting photo.",
            color: "#E8407A",
        },
        {
            num: "03",
            title: "Moteur de Recherche Sémantique",
            desc: "Un moteur de recherche augmenté par IA qui comprend le langage naturel des clients : « quelque chose de fruité et léger ». Suggestions basées sur les goûts, filtres allergènes, saison, résultats classés par pertinence IA.",
            color: "#E8407A",
        },
    ];

    return (
        <div className="page-enter">
            {/* Hero */}
            <section
                style={{
                    ...S.section,
                    paddingTop: 140,
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(ellipse at 70% 50%, #E8407A18 0%, transparent 60%)",
                        pointerEvents: "none",
                    }}
                />
                <div
                    style={{ ...S.container, position: "relative", zIndex: 1 }}
                >
                    <button
                        onClick={() => {
                            setPage("realisations");
                            window.scrollTo(0, 0);
                        }}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--text-muted-dark)",
                            fontFamily: "var(--font-body)",
                            fontSize: 13,
                            marginBottom: 32,
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        ← Toutes les réalisations
                    </button>
                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            marginBottom: 24,
                            flexWrap: "wrap",
                        }}
                    >
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: "#E8407A",
                                background: "#E8407A18",
                                padding: "5px 14px",
                                borderRadius: 100,
                            }}
                        >
                            Site Vitrine
                        </span>
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: "var(--text-muted-dark)",
                                background: "var(--bg-card)",
                                padding: "5px 14px",
                                borderRadius: 100,
                            }}
                        >
                            Génération IA
                        </span>
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: "var(--text-muted-dark)",
                                background: "var(--bg-card)",
                                padding: "5px 14px",
                                borderRadius: 100,
                            }}
                        >
                            2025
                        </span>
                    </div>
                    <h1 style={{ ...S.h1, marginBottom: 16 }}>
                        Monde du{" "}
                        <span style={{ color: "#E8407A" }}>Macaron</span>
                    </h1>
                    <p
                        style={{
                            fontFamily: "var(--font-body)",
                            fontSize: 18,
                            color: "var(--text-muted-dark)",
                            marginBottom: 60,
                            lineHeight: 1.65,
                            maxWidth: 560,
                        }}
                    >
                        Annecy · Salon de Thé — Projet Web 2025
                    </p>
                    <a
                        href="https://macaronsshop.netlify.app/"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            display: "inline-block",
                            marginBottom: 24,
                            padding: "11px 20px",
                            borderRadius: 999,
                            border: "1px solid #E8407A66",
                            color: "#E8407A",
                            textDecoration: "none",
                            fontSize: 13,
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        Visiter Macarons Shop
                    </a>
                    <div
                        style={{
                            marginBottom: 44,
                            padding: "20px 24px",
                            borderRadius: 16,
                            border: "1px solid var(--border-dark)",
                            background: "var(--bg-card)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <img
                            src="/logo-macarons-shop.png"
                            alt="Logo Macarons Shop"
                            style={{
                                width: 220,
                                maxWidth: "60vw",
                                height: "auto",
                                display: "block",
                            }}
                        />
                    </div>
                    {/* Stats */}
                    <div
                        style={{
                            display: "flex",
                            gap: 48,
                            borderTop: "1px solid var(--border-dark)",
                            paddingTop: 40,
                        }}
                    >
                        {[
                            ["3", "Innovations IA intégrées"],
                            ["30+", "Produits générés par IA"],
                            ["100%", "Identité visuelle originale"],
                        ].map(([n, l]) => (
                            <div key={n}>
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontSize: 36,
                                        fontWeight: 800,
                                        color: "#E8407A",
                                    }}
                                >
                                    {n}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "var(--text-muted-dark)",
                                        marginTop: 4,
                                    }}
                                >
                                    {l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Context */}
            <section
                style={{
                    ...S.section,
                    background: "var(--bg-card)",
                    borderTop: "1px solid var(--border-dark)",
                    borderBottom: "1px solid var(--border-dark)",
                }}
            >
                <div
                    style={{
                        ...S.container,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 80,
                        alignItems: "center",
                    }}
                >
                    <div>
                        <span style={{ ...S.label, color: "#E8407A" }}>
                            Le Projet
                        </span>
                        <h2 style={{ ...S.h2, fontSize: 38, marginBottom: 24 }}>
                            Un salon de thé fictif au bord du lac
                        </h2>
                        <p style={{ ...S.body, marginBottom: 16 }}>
                            Site vitrine pour{" "}
                            <strong style={{ color: "var(--text-dark)" }}>
                                Monde du Macaron
                            </strong>
                            , un salon de thé imaginaire d'Annecy spécialisé
                            dans les macarons artisanaux, les gaufres et les
                            crêpes.
                        </p>
                        <p style={{ ...S.body }}>
                            Ce projet est un cas d'école complet : identité
                            visuelle from scratch, catalogue produit 100% généré
                            par IA, et moteur de recherche sémantique augmenté —
                            trois technologies IA au service d'une expérience
                            client premium.
                        </p>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                padding: "20px 24px",
                                borderRadius: 14,
                                border: "1px solid var(--border-dark)",
                                background: "var(--bg-dark)",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 11,
                                    color: "#E8407A",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginBottom: 10,
                                }}
                            >
                                Palette de marque
                            </div>
                            <div style={{ display: "flex", gap: 10 }}>
                                {[
                                    ["#E8407A", "Rose Macaron"],
                                    ["#1A1218", "Charbon"],
                                    ["#FAF6F0", "Crème Ivoire"],
                                    ["#F5C0D3", "Rose Poudré"],
                                    ["#002395", "Bleu France"],
                                    ["#ED2939", "Rouge France"],
                                ].map(([c, n]) => (
                                    <div
                                        key={c}
                                        style={{ textAlign: "center" }}
                                    >
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: 8,
                                                background: c,
                                                marginBottom: 4,
                                                border: "1px solid var(--border-dark)",
                                            }}
                                        />
                                        <div
                                            style={{
                                                fontSize: 9,
                                                color: "var(--text-muted-dark)",
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {n}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div
                            style={{
                                padding: "20px 24px",
                                borderRadius: 14,
                                border: "1px solid var(--border-dark)",
                                background: "var(--bg-dark)",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 11,
                                    color: "#E8407A",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginBottom: 10,
                                }}
                            >
                                Typographies
                            </div>
                            <div style={{ display: "flex", gap: 24 }}>
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontSize: 22,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Playfair Display
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted-dark)",
                                        }}
                                    >
                                        Titres — Élégance
                                    </div>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "var(--font-body)",
                                            fontSize: 18,
                                        }}
                                    >
                                        DM Sans
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted-dark)",
                                        }}
                                    >
                                        Corps — Légèreté
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                padding: "20px 24px",
                                borderRadius: 14,
                                border: "1px solid var(--border-dark)",
                                background: "var(--bg-dark)",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 11,
                                    color: "#E8407A",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    marginBottom: 6,
                                }}
                            >
                                Valeurs de marque
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 8,
                                    flexWrap: "wrap",
                                }}
                            >
                                {[
                                    "Artisanat & authenticité",
                                    "Élégance française",
                                    "Gourmandise & convivialité",
                                ].map((v) => (
                                    <span
                                        key={v}
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted-dark)",
                                            background: "var(--border-dark)",
                                            padding: "4px 12px",
                                            borderRadius: 100,
                                        }}
                                    >
                                        {v}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3 Innovations */}
            <section style={S.section}>
                <div style={S.container}>
                    <div style={{ textAlign: "center", marginBottom: 70 }}>
                        <span style={{ ...S.label, color: "#E8407A" }}>
                            Innovations intégrées
                        </span>
                        <h2 style={S.h2}>
                            3 technologies IA au service du projet
                        </h2>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3,1fr)",
                            gap: 20,
                        }}
                    >
                        {innovations.map((inn) => (
                            <StepCard key={inn.num} {...inn} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Search demo */}
            <section
                style={{
                    ...S.section,
                    background: "var(--bg-card)",
                    borderTop: "1px solid var(--border-dark)",
                }}
            >
                <div style={{ ...S.container, maxWidth: 800 }}>
                    <span style={{ ...S.label, color: "#E8407A" }}>
                        Aperçu — Moteur IA
                    </span>
                    <h2 style={{ ...S.h2, fontSize: 36, marginBottom: 40 }}>
                        Trouver le macaron parfait
                    </h2>
                    <div
                        style={{
                            background: "var(--bg-dark)",
                            borderRadius: 20,
                            padding: 36,
                            border: "1px solid var(--border-dark)",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                background: "var(--bg-card)",
                                borderRadius: 12,
                                padding: "14px 20px",
                                marginBottom: 24,
                                border: "1px solid var(--border-dark)",
                            }}
                        >
                            <span
                                style={{
                                    color: "var(--text-muted-dark)",
                                    fontSize: 18,
                                }}
                            >
                                🔍
                            </span>
                            <span
                                style={{
                                    color: "var(--text-muted-dark)",
                                    fontStyle: "italic",
                                    fontSize: 15,
                                }}
                            >
                                « quelque chose de fruité et léger »
                            </span>
                            <span
                                style={{
                                    marginLeft: "auto",
                                    fontSize: 11,
                                    background: "#E8407A",
                                    color: "white",
                                    padding: "3px 10px",
                                    borderRadius: 100,
                                    fontWeight: 600,
                                }}
                            >
                                IA
                            </span>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                gap: 8,
                                marginBottom: 24,
                                flexWrap: "wrap",
                            }}
                        >
                            {[
                                "Fruité",
                                "Léger",
                                "Chocolat",
                                "Floral",
                                "Sans gluten",
                            ].map((f, i) => (
                                <span
                                    key={f}
                                    style={{
                                        fontSize: 12,
                                        padding: "5px 14px",
                                        borderRadius: 100,
                                        background:
                                            i < 2
                                                ? "#E8407A"
                                                : "var(--bg-card)",
                                        color:
                                            i < 2
                                                ? "white"
                                                : "var(--text-muted-dark)",
                                        border: "1px solid var(--border-dark)",
                                    }}
                                >
                                    {f}
                                </span>
                            ))}
                        </div>
                        {[
                            [
                                "Macaron Framboise",
                                "fruité, acidulé, léger en bouche",
                                "98%",
                            ],
                            [
                                "Macaron Citron Bergamote",
                                "agrumes, fraîcheur, légèreté",
                                "94%",
                            ],
                            [
                                "Macaron Passion Mangue",
                                "exotique, fruité, estival",
                                "89%",
                            ],
                        ].map(([name, desc, score]) => (
                            <div
                                key={name}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "14px 18px",
                                    borderRadius: 12,
                                    background: "var(--bg-card)",
                                    marginBottom: 10,
                                    border: "1px solid var(--border-dark)",
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 15,
                                            marginBottom: 3,
                                        }}
                                    >
                                        {name}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "var(--text-muted-dark)",
                                        }}
                                    >
                                        Correspondance : {desc}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 800,
                                        fontSize: 18,
                                        color: "#E8407A",
                                    }}
                                >
                                    {score}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

// ————— PAGE: ACI-BIL —————
function PageACI({ setPage }) {
    const modules = [
        {
            icon: "🤝",
            title: "Commercial",
            items: [
                "Génération automatique des devis",
                "Pré-remplissage des souscriptions",
                "Signature en ligne",
            ],
        },
        {
            icon: "📋",
            title: "Administratif",
            items: [
                "Souscriptions & antériorité",
                "Garanties souscrites",
                "Suivi des emails",
            ],
        },
        {
            icon: "⚖",
            title: "Sinistre",
            items: [
                "Calcul automatique parts CFE",
                "Avance ou refus des frais",
                "Virements bancaires hebdo.",
            ],
        },
        {
            icon: "🏥",
            title: "Tiers Payant",
            items: [
                "Télétransmission des factures",
                "Transmission des factures",
                "Interface pros de santé",
            ],
        },
        {
            icon: "👤",
            title: "Espace Assuré",
            items: [
                "Suivi des remboursements",
                "Paiement en ligne",
                "Suivi des cotisations",
            ],
        },
        {
            icon: "💰",
            title: "Comptable",
            items: [
                "Génération des appels de cotisations",
                "Statistiques consolidées",
                "Campagnes emailing",
            ],
        },
        {
            icon: "📊",
            title: "Direction",
            items: [
                "Audit trail des actions",
                "Suivi gestionnaires & assurés",
                "Tableaux de bord stratégiques",
            ],
        },
    ];

    const timeline = [
        {
            year: "2016",
            desc: "Démarrage du mandat. Conception de l'architecture et développement des modules commercial & administratif.",
        },
        {
            year: "2017",
            desc: "Mise en production du module sinistre. Premiers remboursements traités via la plateforme.",
        },
        {
            year: "2018",
            desc: "Lancement du module tiers payant. Interface dédiée aux professionnels de santé en Afrique.",
        },
        {
            year: "2019",
            desc: "Déploiement de l'espace assuré et de l'application mobile. Paiement en ligne intégré.",
        },
        {
            year: "2020",
            desc: "Module comptable et automatisation des virements bancaires hebdomadaires. Montée en charge.",
        },
        {
            year: "2021–22",
            desc: "Espace direction, statistiques avancées, emailing & audit trail. Consolidation de la plateforme.",
        },
    ];

    const soinFlow = [
        {
            step: "01",
            title: "Fiche assuré",
            desc: "Identification, contrats actifs, ayants droit et destination de paiement centralisés sur le même écran.",
            accent: "var(--accent)",
        },
        {
            step: "02",
            title: "Bénéficiaire & date",
            desc: "Couverture auto-détectée, devise du soin et alerte de délai de carence en temps réel.",
            accent: "var(--accent-2)",
        },
        {
            step: "03",
            title: "Prestation",
            desc: "Recherche guidée par code/libellé, affichage des taux et plafonds, détection des dépassements.",
            accent: "var(--accent)",
        },
        {
            step: "04",
            title: "Montants & paiement",
            desc: "Calcul automatique du remboursement, subrogation intégrée et NET à payer prêt à valider.",
            accent: "var(--accent-2)",
        },
    ];

    const paymentModes = [
        {
            mode: "Assuré",
            desc: "IBAN de l'assuré pré-rempli et avertissement immédiat en cas de modification récente des coordonnées.",
        },
        {
            mode: "Tiers prestataire",
            desc: "Règlement direct vers l'établissement de soins avec référentiel praticien et subrogation automatique.",
        },
        {
            mode: "Collectivité employeur",
            desc: "Remboursement vers le RIB société pour les contrats collectifs et les scénarios de subrogation totale.",
        },
    ];

    return (
        <div className="page-enter">
            {/* Hero */}
            <section
                style={{
                    ...S.section,
                    paddingTop: 140,
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(ellipse at 70% 50%, oklch(62% 0.22 268 / 0.12) 0%, transparent 60%)",
                        pointerEvents: "none",
                    }}
                />
                <div
                    style={{ ...S.container, position: "relative", zIndex: 1 }}
                >
                    <button
                        onClick={() => {
                            setPage("realisations");
                            window.scrollTo(0, 0);
                        }}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--text-muted-dark)",
                            fontFamily: "var(--font-body)",
                            fontSize: 13,
                            marginBottom: 32,
                            padding: 0,
                        }}
                    >
                        ← Toutes les réalisations
                    </button>
                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            marginBottom: 24,
                            flexWrap: "wrap",
                        }}
                    >
                        {["Logiciel SaaS", "Sur-mesure", "2016 – 2022"].map(
                            (t) => (
                                <span
                                    key={t}
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: "0.1em",
                                        textTransform: "uppercase",
                                        color: "var(--text-muted-dark)",
                                        background: "var(--bg-card)",
                                        padding: "5px 14px",
                                        borderRadius: 100,
                                    }}
                                >
                                    {t}
                                </span>
                            ),
                        )}
                    </div>
                    <h1 style={{ ...S.h1, marginBottom: 16 }}>
                        ACI-BIL{" "}
                        <span style={{ color: "var(--accent)" }}>
                            Burdigala
                        </span>
                    </h1>
                    <p
                        style={{
                            fontFamily: "var(--font-body)",
                            fontSize: 18,
                            color: "var(--text-muted-dark)",
                            marginBottom: 24,
                            lineHeight: 1.65,
                            maxWidth: 640,
                        }}
                    >
                        Six ans de transformation numérique pour une assurance
                        complémentaire santé internationale dédiée aux
                        expatriés.
                    </p>
                    <a
                        href="https://www.aci-bil.fr"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            display: "inline-block",
                            marginBottom: 24,
                            padding: "11px 20px",
                            borderRadius: 999,
                            border: "1px solid var(--accent)",
                            color: "var(--accent)",
                            textDecoration: "none",
                            fontSize: 13,
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        Visiter ACI-BIL
                    </a>
                    <div
                        style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            marginBottom: 60,
                        }}
                    >
                        {[
                            "🌍 Sénégal",
                            "🌍 Côte d'Ivoire",
                            "🌍 Afrique du Nord",
                            "🌐 International",
                        ].map((z) => (
                            <span
                                key={z}
                                style={{
                                    fontSize: 12,
                                    color: "var(--text-muted-dark)",
                                    background: "var(--bg-card)",
                                    padding: "5px 14px",
                                    borderRadius: 100,
                                    border: "1px solid var(--border-dark)",
                                }}
                            >
                                {z}
                            </span>
                        ))}
                    </div>
                    <div
                        style={{
                            marginBottom: 44,
                            padding: "18px 24px",
                            borderRadius: 16,
                            border: "1px solid var(--border-dark)",
                            background: "var(--bg-card)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <img
                            src="/logo-aci-burdigala.png"
                            alt="Logo ACI-BIL Burdigala"
                            style={{
                                width: 280,
                                maxWidth: "68vw",
                                height: "auto",
                                display: "block",
                            }}
                        />
                    </div>
                    <div
                        style={{
                            display: "flex",
                            gap: 60,
                            borderTop: "1px solid var(--border-dark)",
                            paddingTop: 40,
                        }}
                    >
                        {[
                            ["47 M€", "de déboursements traités"],
                            ["34 M€", "remboursés aux assurés"],
                            ["5 000+", "demandes traitées avec succès"],
                            ["7", "espaces métier intégrés"],
                        ].map(([n, l]) => (
                            <div key={n}>
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontSize: 34,
                                        fontWeight: 800,
                                        color: "var(--accent)",
                                    }}
                                >
                                    {n}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: "var(--text-muted-dark)",
                                        marginTop: 4,
                                    }}
                                >
                                    {l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Context */}
            <section
                style={{
                    ...S.section,
                    background: "var(--bg-card)",
                    borderTop: "1px solid var(--border-dark)",
                    borderBottom: "1px solid var(--border-dark)",
                }}
            >
                <div
                    style={{
                        ...S.container,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 80,
                        alignItems: "start",
                    }}
                >
                    <div>
                        <span style={S.label}>Contexte du projet</span>
                        <h2 style={{ ...S.h2, fontSize: 36, marginBottom: 24 }}>
                            Une assurance de confiance pour les expatriés du
                            monde
                        </h2>
                        <p style={{ ...S.body, marginBottom: 16 }}>
                            ACI-BIL (Burdigala International Assurances) propose
                            des complémentaires santé dédiées aux expatriés,
                            avec une forte présence en Afrique Subsaharienne et
                            au Maghreb.
                        </p>
                        <p style={{ ...S.body, marginBottom: 16 }}>
                            Face à la complexité de la gestion multi-pays,
                            multi-devises et multi-bénéficiaires, la société a
                            mandaté le développement d'un logiciel entièrement
                            sur-mesure couvrant l'ensemble des fonctions métier.
                        </p>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                        }}
                    >
                        {[
                            ["TYPE", "Logiciel SaaS sur-mesure"],
                            ["DURÉE", "6 ans de développement continu"],
                            ["PÉRIMÈTRE", "7 espaces métier intégrés"],
                            [
                                "LIVRABLES",
                                "Web app · App mobile · Documents & identité",
                            ],
                        ].map(([k, v]) => (
                            <div
                                key={k}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "16px 24px",
                                    borderRadius: 12,
                                    border: "1px solid var(--border-dark)",
                                    background: "var(--bg-dark)",
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                        color: "var(--text-muted-dark)",
                                    }}
                                >
                                    {k}
                                </span>
                                <span style={{ fontSize: 15, fontWeight: 500 }}>
                                    {v}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 7 modules */}
            <section style={S.section}>
                <div style={S.container}>
                    <div style={{ marginBottom: 60 }}>
                        <span style={S.label}>Architecture fonctionnelle</span>
                        <h2 style={S.h2}>7 espaces métier intégrés</h2>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4,1fr)",
                            gap: 16,
                        }}
                    >
                        {modules.map((m, i) => (
                            <ModuleCard
                                key={m.title}
                                {...m}
                                accent={
                                    i % 2 === 0
                                        ? "var(--accent)"
                                        : "var(--accent-2)"
                                }
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Livrables */}
            <section
                style={{
                    ...S.section,
                    background: "var(--bg-card)",
                    borderTop: "1px solid var(--border-dark)",
                    borderBottom: "1px solid var(--border-dark)",
                }}
            >
                <div style={S.container}>
                    <span style={S.label}>Livrables</span>
                    <h2 style={{ ...S.h2, marginBottom: 48 }}>
                        Écosystème complet, de l'interface au terrain
                    </h2>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3,1fr)",
                            gap: 20,
                        }}
                    >
                        {[
                            [
                                "🖥️",
                                "Application Web",
                                "Plateforme de Gestion",
                                "Application web multi-profils avec gestion des sinistres, contrats, cotisations et virements bancaires automatisés.",
                            ],
                            [
                                "📱",
                                "Application Mobile",
                                "App Assuré",
                                "App mobile pour soumettre des remboursements, suivre les dossiers et payer les cotisations depuis n'importe où.",
                            ],
                            [
                                "🪪",
                                "Identité & Documents",
                                "Cartes & Certificats",
                                "Génération automatique des cartes d'assurés, certificats de garantie, bons d'examens et documents d'adhésion.",
                            ],
                        ].map(([icon, type, title, desc]) => (
                            <div
                                key={title}
                                style={{
                                    padding: 32,
                                    borderRadius: 20,
                                    border: "1px solid var(--border-dark)",
                                    background: "var(--bg-dark)",
                                }}
                            >
                                <div style={{ fontSize: 28, marginBottom: 16 }}>
                                    {icon}
                                </div>
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: "var(--accent)",
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                        marginBottom: 8,
                                    }}
                                >
                                    {type}
                                </div>
                                <h3
                                    style={{
                                        ...S.h3,
                                        fontSize: 20,
                                        marginBottom: 12,
                                    }}
                                >
                                    {title}
                                </h3>
                                <p style={{ ...S.body, fontSize: 14 }}>
                                    {desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section style={S.section}>
                <div style={S.container}>
                    <span style={S.label}>Chronologie</span>
                    <h2 style={{ ...S.h2, marginBottom: 60 }}>
                        6 ans de développement continu
                    </h2>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                        }}
                    >
                        {timeline.map((t) => (
                            <div
                                key={t.year}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "120px 1fr",
                                    gap: 32,
                                    paddingBottom: 36,
                                    borderLeft: "2px solid var(--border-dark)",
                                    marginLeft: 16,
                                    paddingLeft: 36,
                                    position: "relative",
                                }}
                            >
                                <div
                                    style={{
                                        position: "absolute",
                                        left: -9,
                                        top: 4,
                                        width: 16,
                                        height: 16,
                                        borderRadius: "50%",
                                        background: "var(--accent)",
                                        border: "3px solid var(--bg-dark)",
                                    }}
                                />
                                <div
                                    style={{
                                        fontFamily: "var(--font-display)",
                                        fontWeight: 800,
                                        fontSize: 20,
                                        color: "var(--accent)",
                                        paddingTop: 2,
                                    }}
                                >
                                    {t.year}
                                </div>
                                <p style={{ ...S.body, fontSize: 15 }}>
                                    {t.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SAISIE DES SOINS — extension inspirée de la présentation Burdigala */}
            <section
                style={{
                    ...S.section,
                    background: "var(--bg-card)",
                    borderTop: "1px solid var(--border-dark)",
                }}
            >
                <div style={S.container}>
                    <span style={S.label}>Saisie des soins</span>
                    <h2 style={{ ...S.h2, marginBottom: 20 }}>
                        Flux guidé de remboursement
                    </h2>
                    <p
                        style={{
                            ...S.body,
                            maxWidth: 860,
                            marginBottom: 46,
                        }}
                    >
                        Inspiré des écrans opérateurs Burdigala : un parcours de
                        saisie en 4 étapes, pensé pour réduire les erreurs et
                        accélérer le traitement des dossiers.
                    </p>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: 16,
                            marginBottom: 36,
                        }}
                    >
                        {soinFlow.map((item) => (
                            <div
                                key={item.step}
                                style={{
                                    border: "1px solid var(--border-dark)",
                                    borderRadius: 16,
                                    background: "var(--bg-dark)",
                                    padding: 22,
                                }}
                            >
                                <div
                                    style={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 10,
                                        background: `${item.accent}22`,
                                        color: item.accent,
                                        fontWeight: 800,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 14,
                                        fontSize: 13,
                                    }}
                                >
                                    {item.step}
                                </div>
                                <h3
                                    style={{
                                        ...S.h3,
                                        fontSize: 20,
                                        marginBottom: 10,
                                    }}
                                >
                                    {item.title}
                                </h3>
                                <p style={{ ...S.body, fontSize: 14 }}>
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 1fr",
                            gap: 20,
                            marginBottom: 20,
                        }}
                    >
                        <div
                            style={{
                                border: "1px solid var(--border-dark)",
                                borderRadius: 16,
                                background: "var(--bg-dark)",
                                padding: 28,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    color: "var(--accent-2)",
                                    fontWeight: 700,
                                    marginBottom: 14,
                                }}
                            >
                                Résultats observés
                            </div>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 18,
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontSize: 42,
                                            fontWeight: 800,
                                            color: "var(--accent)",
                                        }}
                                    >
                                        +50%
                                    </div>
                                    <p style={{ ...S.body, fontSize: 14 }}>
                                        Productivité des gestionnaires grâce au
                                        pré-remplissage et aux contrôles
                                        embarqués.
                                    </p>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontSize: 42,
                                            fontWeight: 800,
                                            color: "var(--accent-2)",
                                        }}
                                    >
                                        -80%
                                    </div>
                                    <p style={{ ...S.body, fontSize: 14 }}>
                                        Erreurs de saisie via les alertes de
                                        carence, plafonds et incohérences de
                                        remboursement.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                border: "1px solid var(--border-dark)",
                                borderRadius: 16,
                                background: "var(--bg-dark)",
                                padding: 24,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.1em",
                                    color: "var(--accent)",
                                    fontWeight: 700,
                                    marginBottom: 14,
                                }}
                            >
                                Modes de règlement
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 14,
                                }}
                            >
                                {paymentModes.map((mode) => (
                                    <div key={mode.mode}>
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 14,
                                                marginBottom: 4,
                                            }}
                                        >
                                            {mode.mode}
                                        </div>
                                        <p style={{ ...S.body, fontSize: 13 }}>
                                            {mode.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ModuleCard({ icon, title, items, accent }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            style={{
                padding: 24,
                borderRadius: 16,
                border: `1px solid ${hovered ? accent : "var(--border-dark)"}`,
                background: hovered ? "var(--bg-card)" : "transparent",
                transition: "all 0.25s",
            }}
        >
            <div style={{ fontSize: 24, marginBottom: 12 }}>{icon}</div>
            <div
                style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: 16,
                    marginBottom: 14,
                }}
            >
                {title}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {items.map((item) => (
                    <div
                        key={item}
                        style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "flex-start",
                        }}
                    >
                        <span
                            style={{
                                color: accent,
                                fontSize: 10,
                                marginTop: 4,
                                flexShrink: 0,
                            }}
                        >
                            ▶
                        </span>
                        <span
                            style={{
                                fontSize: 12,
                                color: "var(--text-muted-dark)",
                                lineHeight: 1.5,
                            }}
                        >
                            {item}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ————— APP ROOT —————
function App() {
    const [page, setPage] = useState(
        () => localStorage.getItem("agenceIA_page") || "home",
    );

    useEffect(() => {
        localStorage.setItem("agenceIA_page", page);
    }, [page]);

    const pages = {
        home: <PageHome setPage={setPage} />,
        expertises: <PageExpertises setPage={setPage} />,
        realisations: <PageRealisations setPage={setPage} />,
        macaron: <PageMacaron setPage={setPage} />,
        aci: <PageACI setPage={setPage} />,
        geo: <PageGEO setPage={setPage} />,
        formation: <PageFormation setPage={setPage} />,
        contact: <PageContact />,
        "mentions-legales": <PageMentionsLegales />,
    };

    // Tweaks setup
    useEffect(() => {
        applyTweaks(TWEAK_DEFAULTS);
        const panel = document.getElementById("tweaks-panel");
        if (!panel) return undefined;

        const onMessage = (e) => {
            if (e.data?.type === "__activate_edit_mode")
                panel.classList.add("visible");
            if (e.data?.type === "__deactivate_edit_mode")
                panel.classList.remove("visible");
        };
        window.addEventListener("message", onMessage);

        // Swatches
        document.querySelectorAll(".swatch").forEach((sw) => {
            sw.addEventListener("click", () => {
                document
                    .querySelectorAll(".swatch")
                    .forEach((s) => s.classList.remove("active"));
                sw.classList.add("active");
                const t = {
                    ...TWEAK_DEFAULTS,
                    accent: sw.dataset.accent,
                    accent2: sw.dataset.accent2,
                };
                applyTweaks(t);
            });
        });

        document.getElementById("tweak-bg").addEventListener("change", (e) => {
            applyTweaks({ ...TWEAK_DEFAULTS, bg: e.target.value });
        });

        document
            .getElementById("tweak-font")
            .addEventListener("change", (e) => {
                const pair = FONT_PAIRS[e.target.value];
                if (pair) {
                    document.documentElement.style.setProperty(
                        "--font-display",
                        pair.display,
                    );
                    document.documentElement.style.setProperty(
                        "--font-body",
                        pair.body,
                    );
                }
            });

        document
            .getElementById("tweak-fontsize")
            .addEventListener("input", (e) => {
                document.body.style.fontSize = e.target.value + "px";
            });

        document
            .getElementById("tweak-spacing")
            .addEventListener("input", (e) => {
                document.documentElement.style.setProperty(
                    "--section-gap",
                    e.target.value + "px",
                );
            });
        return () => {
            window.removeEventListener("message", onMessage);
        };
    }, []);

    return (
        <>
            <TweaksPanel>
                <TweakTitle>Tweaks</TweakTitle>
                <TweakRow>
                    <TweakLabel>Typographie</TweakLabel>
                    <TweakSelect id="tweak-font">
                        <option value="syne">Syne + Jakarta - Futuriste</option>
                        <option value="playfair">
                            Playfair + DM Sans - Elegant
                        </option>
                        <option value="space">
                            Space Grotesk + Manrope - Tech
                        </option>
                        <option value="cormorant">
                            Cormorant + Outfit - Luxe minimal
                        </option>
                        <option value="barlow">
                            Barlow Condensed + Barlow - Incisif
                        </option>
                    </TweakSelect>
                </TweakRow>
                <TweakRow>
                    <TweakLabel>Couleur d'accent</TweakLabel>
                    <TweakColors>
                        <Swatch
                            className="swatch active"
                            data-accent="oklch(62% 0.22 268)"
                            data-accent2="oklch(68% 0.17 162)"
                            style={{ background: "oklch(62% 0.22 268)" }}
                        />
                        <Swatch
                            className="swatch"
                            data-accent="oklch(62% 0.22 30)"
                            data-accent2="oklch(68% 0.17 200)"
                            style={{ background: "oklch(62% 0.22 30)" }}
                        />
                        <Swatch
                            className="swatch"
                            data-accent="oklch(62% 0.22 145)"
                            data-accent2="oklch(68% 0.17 290)"
                            style={{ background: "oklch(62% 0.22 145)" }}
                        />
                        <Swatch
                            className="swatch"
                            data-accent="oklch(62% 0.22 320)"
                            data-accent2="oklch(68% 0.17 60)"
                            style={{ background: "oklch(62% 0.22 320)" }}
                        />
                    </TweakColors>
                </TweakRow>
                <TweakRow>
                    <TweakLabel>Style fond</TweakLabel>
                    <TweakSelect id="tweak-bg">
                        <option value="dark">Sombre (defaut)</option>
                        <option value="light">Clair</option>
                    </TweakSelect>
                </TweakRow>
                <TweakRow>
                    <TweakLabel>Taille texte corps</TweakLabel>
                    <TweakRange
                        id="tweak-fontsize"
                        min="14"
                        max="19"
                        step="1"
                        defaultValue="16"
                    />
                </TweakRow>
                <TweakRow>
                    <TweakLabel>Espacement sections</TweakLabel>
                    <TweakRange
                        id="tweak-spacing"
                        min="60"
                        max="140"
                        step="10"
                        defaultValue="100"
                    />
                </TweakRow>
            </TweaksPanel>
            <NavBar page={page} setPage={setPage} />
            <main key={page}>{pages[page]}</main>
            <Footer setPage={setPage} />
        </>
    );
}

export default App;
